import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { BalanceResult } from '@balance/domain';
import { api, ApiError, type AuthStatus, type Catalog, type Movements } from './api.js';

export interface Toast {
  id: number;
  text: string;
  kind: 'ok' | 'error';
}

interface AppState {
  loading: boolean;
  error: string | null;
  catalog: Catalog | null;
  balance: BalanceResult | null;
  movements: Movements | null;
  period: string;
  setPeriod: (p: string) => void;
  refresh: () => Promise<void>;
  toast: (text: string, kind?: Toast['kind']) => void;
  toasts: Toast[];
  reduced: boolean;
  setReduced: (v: boolean) => void;
  userKey: string;
  openConcept: (id: string) => void;
  conceptSheetId: string | null;
  closeConcept: () => void;
  run: <T>(fn: () => Promise<T>, okText?: string) => Promise<T | undefined>;
  needsHousehold: boolean;
  needsLogin: boolean;
  authEnabled: boolean;
  auth: AuthStatus | null;
  logout: () => Promise<void>;
}

const Ctx = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [balance, setBalance] = useState<BalanceResult | null>(null);
  const [movements, setMovements] = useState<Movements | null>(null);
  const [needsHousehold, setNeedsHousehold] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [authEnabled, setAuthEnabled] = useState(false);
  const [auth, setAuth] = useState<AuthStatus | null>(null);
  const [period, setPeriodState] = useState<string>(() => localStorage.getItem('balance.period') ?? '');
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [reduced, setReducedState] = useState<boolean>(() => localStorage.getItem('balance.reduced') === '1');
  const [conceptSheetId, setConceptSheetId] = useState<string | null>(null);
  const toastId = useRef(0);
  const userKey = useMemo(() => localStorage.getItem('balance.userKey') ?? 'hogar', []);

  const toast = useCallback((text: string, kind: Toast['kind'] = 'ok') => {
    const id = ++toastId.current;
    setToasts((t) => [...t, { id, text, kind }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'error' ? 6000 : 3500);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const status: AuthStatus = await api.authStatus().catch(() => ({ mode: 'open', enabled: false, authenticated: true, user: null }));
      setAuth(status);
      setAuthEnabled(status.enabled);
      if (!status.authenticated) {
        setNeedsLogin(true);
        setError(null);
        return;
      }
      setNeedsLogin(false);
      const [c, b, m] = await Promise.all([api.catalog(), api.balance(), api.movements()]);
      setCatalog(c);
      setBalance(b);
      setMovements(m);
      setNeedsHousehold(false);
      setError(null);
      setPeriodState((p) => (p && b.periods.some((x) => x.period === p) ? p : b.currentPeriod));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setNeedsLogin(true);
        setError(null);
      } else if (e instanceof ApiError && e.status === 409) {
        setNeedsHousehold(true);
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : 'Error desconocido');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    document.body.classList.toggle('reduced', reduced);
  }, [reduced]);

  const setPeriod = useCallback((p: string) => {
    setPeriodState(p);
    localStorage.setItem('balance.period', p);
  }, []);
  const setReduced = useCallback((v: boolean) => {
    setReducedState(v);
    localStorage.setItem('balance.reduced', v ? '1' : '0');
  }, []);

  const run = useCallback(
    async <T,>(fn: () => Promise<T>, okText?: string): Promise<T | undefined> => {
      try {
        const result = await fn();
        await refresh();
        if (okText) toast(okText);
        return result;
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Error', 'error');
        return undefined;
      }
    },
    [refresh, toast],
  );

  const value: AppState = {
    loading,
    error,
    catalog,
    balance,
    movements,
    period,
    setPeriod,
    refresh,
    toast,
    toasts,
    reduced,
    setReduced,
    userKey,
    openConcept: setConceptSheetId,
    conceptSheetId,
    closeConcept: () => setConceptSheetId(null),
    run,
    needsHousehold,
    needsLogin,
    authEnabled,
    auth,
    logout: async () => {
      await api.logout().catch(() => undefined);
      setCatalog(null);
      setBalance(null);
      setMovements(null);
      setNeedsLogin(true);
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp fuera de AppProvider');
  return v;
}
